import React from 'react';
import type { AppProps } from 'next/app';
import { ApolloProvider } from '@apollo/client';
import { useApollo } from '../utils/apollo';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import GlobalStyles from '../styles/global';
import theme from '../styles/theme';
import { LocaleProvider } from '../providers/localeProvider';
import seoPt from '../../next-seo-pt.config';
import seoEn from '../../next-seo-en.config';
import { DefaultSeo } from 'next-seo';
import { useRouter } from 'next/router';
import Head from 'next/head';

import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import isPropValid from '@emotion/is-prop-valid';
import Script from 'next/script';
import RollbarProvider from '../providers/RollbarProvider';
import { getRollbar } from '../utils/logging';
import dynamic from 'next/dynamic';
import HubspotChat from '../components/elements/HubspotChat';
import { initFacebookPixel, trackFacebookPageView } from '../utils/facebookPixel';

// Client-only: the floating WhatsApp button must never exist in the
// server-rendered HTML. If it did, it would be painted before the page finishes
// hydrating, leaving a window where the button is visible but its onClick is not
// yet attached — the "sometimes it opens, sometimes it doesn't" bug (issue
// #1376). Rendering it only on the client guarantees it appears with its handler
// already wired.
const WhatsAppChat = dynamic(() => import('../components/elements/WhatsAppChat'), { ssr: false });

/**
 * MyApp
 */
function MyApp({ Component, pageProps }: AppProps) {
  // Check the URL starts with 'http://xxxxx' protocol, if it does then redirect to 'https://xxxxx' url of same resource
  if (process.browser && typeof window !== 'undefined') {
    const httpTokens = /^http:\/\/(.*)$/.exec(window.location.href);
    if (httpTokens) {
      const isLocalhost = httpTokens[1].includes('localhost');
      if (!isLocalhost) window.location.replace('https://' + httpTokens[1]);
    }
  }

  const apolloClient = useApollo(pageProps.initialApolloState);
  const router = useRouter();
  const SEO = router.locale === 'pt' ? seoPt : seoEn;

  function storePathValues() {
    const storage = globalThis?.sessionStorage;
    if (!storage) return;
    // Set the previous path as the value of the current path.
    const prevPath = storage.getItem('currentPath');
    storage.setItem('prevPath', `${prevPath}}`);
    // Set the current path value by looking at the browser's location object.
    storage.setItem('currentPath', globalThis.location.pathname);
  }

  React.useEffect(() => storePathValues, [router.asPath]);

  React.useEffect(() => {
    let isMounted = true;
    const handleRouteChange = () => {
      void trackFacebookPageView();
    };

    const setupFacebookPixel = async () => {
      const initialized = await initFacebookPixel(process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID || '');
      if (!initialized || !isMounted) return;

      await trackFacebookPageView();
      router.events.on('routeChangeComplete', handleRouteChange);
    };

    void setupFacebookPixel();

    return () => {
      isMounted = false;
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  const rollbarInstance = getRollbar();

  return (
    <>
      {/* Contact channel by site locale: EN shows the HubSpot chat, PT shows the
          WhatsApp button (WhatsAppChat). HubspotChat loads the HubSpot script and
          keeps its chat launcher hidden on PT. */}
      <HubspotChat />
      {/* Only add the gtag script if configured */}
      {process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS && (
        <>
          <Script
            id="gtag-loader"
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}`}
          />
          <Script id="gtag-script" strategy="afterInteractive">
            {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}', {
                page_path: window.location.pathname,
                });
            `}
          </Script>
        </>
      )}
      {/* linkedIn Tag Script */}
      <Script id="linkedin-script" strategy="lazyOnload">
        {`
        _linkedin_partner_id = "${process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID || ''}";
        window._linkedin_data_partner_ids = 
        window._linkedin_data_partner_ids || [];                             
        window._linkedin_data_partner_ids.push(_linkedin_partner_id);
        (function(l) { 
            if (!l){window.lintrk = function(a,b) 
        {window.lintrk.q.push([a,b])};
        window.lintrk.q=[]} var s = 
        document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";
        b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);})(window.lintrk);
      `}
      </Script>
      <Head>
        <noscript>
          {/*eslint-disable-next-line @next/next/no-img-element */}
          <img
            height="1"
            width="1"
            style={{ display: 'none' }}
            alt=""
            src={`https://px.ads.linkedin.com/collect/?pid=${
              process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID || ''
            }&fmt=gif`}
          />
        </noscript>
      </Head>
      <DefaultSeo {...SEO} />
      <RollbarProvider instance={rollbarInstance}>
        <ThemeProvider theme={theme}>
          <StyleSheetManager shouldForwardProp={isPropValid}>
            <GlobalStyles />
            <ApolloProvider client={apolloClient}>
              <LocaleProvider locale="en">
                <GoogleReCaptchaProvider
                  reCaptchaKey={process.env.NEXT_PUBLIC_API_KEY_CAPTCHA as string}
                  scriptProps={{ defer: true, async: true }}
                >
                  <Component {...pageProps} />
                  <WhatsAppChat />
                </GoogleReCaptchaProvider>
              </LocaleProvider>
            </ApolloProvider>
          </StyleSheetManager>
        </ThemeProvider>
      </RollbarProvider>
    </>
  );
}

export default MyApp;
